import { createBrowserRouter, Navigate } from 'react-router'
import { AuthGuard, GuestGuard, RoleGuard } from '@/components/guards'
import { AuthLayout, AppLayout } from '@/components/layouts'
import {
  LoginPage,
  RegisterPage,
  AcceptInvitePage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  VerifyEmailPendingPage,
  DashboardPage,
  NotFoundPage,
  PaymentPage,
  PaymentSuccessPage,
  RouteErrorPage,
} from '@/pages'

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      // Public routes (guest only - redirect if authenticated)
      {
        element: <GuestGuard />,
        errorElement: <RouteErrorPage />,
        children: [
          {
            element: <AuthLayout />,
            errorElement: <RouteErrorPage />,
            children: [
              {
                path: '/login',
                element: <LoginPage />,
              },
              {
                path: '/register',
                element: <RegisterPage />,
              },
              {
                path: '/accept-invite',
                element: <AcceptInvitePage />,
              },
              {
                path: '/forgot-password',
                element: <ForgotPasswordPage />,
              },
              {
                path: '/reset-password',
                element: <ResetPasswordPage />,
              },
            ],
          },
        ],
      },

      // Public standalone pages (no auth required)
      {
        path: '/privacy-policy',
        lazy: () =>
          import('@/pages/public/PrivacyPolicyPage').then((m) => ({
            element: <m.PrivacyPolicyPage />,
          })),
      },

      // Email verification (no guard - accessible to both guests and authenticated users)
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/auth/verify-email',
            element: <VerifyEmailPage />,
          },
          {
            path: '/verify-email-pending',
            element: <VerifyEmailPendingPage />,
          },
        ],
      },

      // Payment pages (authenticated but needs subscription)
      {
        path: '/payment',
        element: <PaymentPage />,
      },
      {
        path: '/payment/success',
        element: <PaymentSuccessPage />,
      },

      // Protected routes (authenticated only)
      {
        element: <AuthGuard />,
        errorElement: <RouteErrorPage />,
        children: [
          {
            element: <AppLayout />,
            errorElement: <RouteErrorPage />,
            children: [
              {
                path: '/dashboard',
                element: <DashboardPage />,
              },
              {
                path: '/patients',
                lazy: () =>
                  import('@/pages/patients').then((m) => ({ element: <m.PatientsPage /> })),
              },
              // Patients detail + write — OWNER, ADMIN, MANAGER
              {
                element: <RoleGuard allowedRoles={['OWNER', 'ADMIN', 'MANAGER']} />,
                children: [
                  {
                    path: '/patients/:id',
                    lazy: () =>
                      import('@/pages/patients').then((m) => ({
                        element: <m.PatientDetailPage />,
                      })),
                  },
                  {
                    path: '/patients/new',
                    lazy: () =>
                      import('@/pages/patients').then((m) => ({ element: <m.AddPatientPage /> })),
                  },
                  {
                    path: '/patients/:id/edit',
                    lazy: () =>
                      import('@/pages/patients').then((m) => ({ element: <m.EditPatientPage /> })),
                  },
                ],
              },
              // Staff — OWNER, ADMIN, MANAGER
              {
                element: <RoleGuard allowedRoles={['OWNER', 'ADMIN', 'MANAGER']} />,
                children: [
                  {
                    path: '/staff',
                    lazy: () => import('@/pages/staff').then((m) => ({ element: <m.StaffPage /> })),
                  },
                  {
                    path: '/staff/:id',
                    lazy: () =>
                      import('@/pages/staff').then((m) => ({ element: <m.StaffDetailPage /> })),
                  },
                ],
              },
              // Staff write — OWNER, ADMIN
              {
                element: <RoleGuard allowedRoles={['OWNER', 'ADMIN']} />,
                children: [
                  {
                    path: '/staff/new',
                    lazy: () =>
                      import('@/pages/staff').then((m) => ({ element: <m.AddStaffPage /> })),
                  },
                  {
                    path: '/staff/:id/edit',
                    lazy: () =>
                      import('@/pages/staff').then((m) => ({ element: <m.EditStaffPage /> })),
                  },
                ],
              },
              // Documents — OWNER, ADMIN, MANAGER
              {
                element: <RoleGuard allowedRoles={['OWNER', 'ADMIN', 'MANAGER']} />,
                children: [
                  {
                    path: '/documents',
                    lazy: () =>
                      import('@/pages/documents').then((m) => ({ element: <m.DocumentsPage /> })),
                  },
                  {
                    path: '/documents/:id',
                    lazy: () =>
                      import('@/pages/documents').then((m) => ({
                        element: <m.DocumentDetailPage />,
                      })),
                  },
                ],
              },
              // Financials — OWNER, ADMIN
              {
                element: <RoleGuard allowedRoles={['OWNER', 'ADMIN']} />,
                children: [
                  {
                    path: '/financials',
                    lazy: () =>
                      import('@/pages/financials').then((m) => ({ element: <m.FinancialsPage /> })),
                  },
                  {
                    path: '/financials/transactions',
                    lazy: () =>
                      import('@/pages/financials').then((m) => ({
                        element: <m.TransactionsPage />,
                      })),
                  },
                ],
              },
              // Chat — OWNER, ADMIN, MANAGER
              {
                element: <RoleGuard allowedRoles={['OWNER', 'ADMIN', 'MANAGER']} />,
                children: [
                  {
                    path: '/chat',
                    lazy: () => import('@/pages/chat').then((m) => ({ element: <m.ChatPage /> })),
                  },
                ],
              },
              {
                path: '/schedule',
                lazy: () =>
                  import('@/pages/schedule').then((m) => ({ element: <m.SchedulePage /> })),
              },
              {
                path: '/settings',
                lazy: () =>
                  import('@/pages/settings').then((m) => ({ element: <m.SettingsPage /> })),
              },
              {
                path: '/help',
                lazy: () => import('@/pages/help').then((m) => ({ element: <m.HelpPage /> })),
              },
            ],
          },
        ],
      },

      // Root redirect
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />,
      },

      // 404 - Not Found
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ], // end root errorElement children
  },
])
